import { useEffect, useRef, useState } from 'react';
import {
    AmbiguousDecimalReadings,
    ambiguousDecimalMessage,
    ambiguousDecimalReadings,
    isDecimalEntryText,
    parseDecimalInput,
} from '~/utils/decimalInput';

/** Why the entry a field shows is not accepted, or an edit was not taken. */
export type DecimalEntryRefusal =
    /** An edit holding a character no number is written with, not taken. */
    | { readonly kind: 'notEntered'; readonly entry: string }
    /** The entry names two numbers the field takes (`1,500`). */
    | {
          readonly kind: 'ambiguous';
          readonly text: string;
          readonly readings: AmbiguousDecimalReadings;
      }
    /** The entry is no number under the application's rule. */
    | { readonly kind: 'notANumber' }
    /** The entry has more decimals than the field takes. */
    | { readonly kind: 'tooManyDecimals' }
    /** The entry is a number outside [min, max]. */
    | { readonly kind: 'outOfRange' };

export interface DecimalEntryOptions {
    /** The value in effect, which the field shows while it is not edited. */
    readonly value: number | null;
    /** Applies a value the entry names, or the one from before a refused entry. */
    readonly onChange: (value: number | null) => void;
    readonly min?: number;
    readonly max?: number;
    /** The most decimals the field takes; trailing zeros are not counted. */
    readonly maxFractionDigits?: number;
    /** How the field writes a value: what it shows while not edited. */
    readonly format: (value: number | null) => string;
    /**
     * Whether a number is applied as it is typed. When it is, the start of an
     * entry is applied on its way (`1` of `1.000,5`), and the value from
     * before the edit is applied again from the first key that makes the
     * entry one the field refuses, so no part of a refused entry stays
     * applied. Otherwise the entry is applied when the field is left.
     */
    readonly applyWhileTyping: boolean;
    /**
     * Whether an edit that brings a character no number is written with
     * (`1 500`, `+1,5`, `2a`) is refused, keeping the text the field held and
     * saying the edit was not entered. Otherwise the edit is taken and the
     * entry shown, marked as no number. Digits and separators are taken
     * either way: refusing a separator joins the digits around it (`1,5` →
     * `15`), and refusing a digit shortens the number.
     */
    readonly refuseNonEntryCharacters: boolean;
}

export interface DecimalEntry {
    /** What the field shows: the entry while it is edited, else the value. */
    readonly text: string;
    /** Whether the field shows an entry the user typed. */
    readonly isTouched: boolean;
    /** Whether the field shows an entry it does not accept. */
    readonly isInvalid: boolean;
    /** The number the entry names and the field accepts, while it is edited. */
    readonly accepted: number | null;
    /** Why the entry is not accepted, or the last edit was not taken. */
    readonly refusal: DecimalEntryRefusal | null;
    /** The field was entered: the edit to come starts from the value shown. */
    readonly onFocus: () => void;
    /** The field's text was edited to `text`. */
    readonly onTextChange: (text: string) => void;
    /** The field was left: the entry is applied, or the value from before. */
    readonly onBlur: () => void;
    /**
     * A value was applied by other means (a step, a slider drag): it ends the
     * edit and is the value from before the next one.
     */
    readonly apply: (value: number) => void;
}

/**
 * The typed-entry state of a numeric field, under the application's rule for
 * reading a number ({@link parseDecimalInput}): the text it shows, whether
 * and why the entry is refused, and what it applies when.
 *
 * An edit runs from the first keystroke to leaving the field. It remembers
 * the value from before it, so that a refused entry can put it back, and the
 * value it last applied, so that the owner echoing that value through `value`
 * does not end the edit and replace the entry being typed. Any other change
 * of `value` from outside ends the edit.
 */
export function useDecimalEntry({
    value,
    onChange,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    maxFractionDigits = Number.POSITIVE_INFINITY,
    format,
    applyWhileTyping,
    refuseNonEntryCharacters,
}: DecimalEntryOptions): DecimalEntry {
    const [entryText, setEntryText] = useState('');
    const [isTouched, setIsTouched] = useState(false);
    const [refusedEntry, setRefusedEntry] = useState<string | null>(null);
    const entryTextRef = useRef(entryText);
    entryTextRef.current = entryText;

    // The edit in progress: the value from before it, and the value it last
    // applied through onChange.
    const editRef = useRef<{
        readonly before: number | null;
        applied: number | null;
    } | null>(null);
    // The value in effect: the latest of the value shown and the values
    // applied, which the owner may show through `value` only later.
    const inEffectRef = useRef(value);

    useEffect(() => {
        inEffectRef.current = value;
        if (editRef.current !== null && editRef.current.applied === value) {
            return;
        }
        const entryValue = parseDecimalInput(entryTextRef.current);
        if (entryValue === null || entryValue !== value) {
            editRef.current = null;
            setIsTouched(false);
        }
    }, [value]);

    const isInRange = (candidate: number) =>
        candidate >= min && candidate <= max;

    /** How the field reads `text`: the number it accepts, or why none. */
    const read = (
        text: string
    ): {
        readonly accepted: number | null;
        readonly refusal: DecimalEntryRefusal | null;
    } => {
        const number = parseDecimalInput(text);
        if (number === null) {
            return { accepted: null, refusal: { kind: 'notANumber' } };
        }
        const readings = ambiguousDecimalReadings(
            text,
            isInRange,
            maxFractionDigits
        );
        if (readings !== null) {
            return {
                accepted: null,
                refusal: { kind: 'ambiguous', text, readings },
            };
        }
        if (parseDecimalInput(text, maxFractionDigits) === null) {
            return { accepted: null, refusal: { kind: 'tooManyDecimals' } };
        }
        if (!isInRange(number)) {
            return { accepted: null, refusal: { kind: 'outOfRange' } };
        }
        return { accepted: number, refusal: null };
    };

    const applyIfChanged = (next: number | null) => {
        if (editRef.current !== null) {
            editRef.current.applied = next;
        }
        if (next !== inEffectRef.current) {
            inEffectRef.current = next;
            onChange(next);
        }
    };

    // Applies the value from before the edit again when the edit applied
    // another one: the field shows, or was left with, an entry it does not
    // accept, and the part of it applied earlier must not stay in effect.
    const restoreValueBeforeEdit = () => {
        const edit = editRef.current;
        if (edit !== null && edit.applied !== edit.before) {
            applyIfChanged(edit.before);
        }
    };

    const beginEdit = () => {
        editRef.current ??= {
            before: inEffectRef.current,
            applied: inEffectRef.current,
        };
    };

    const onFocus = () => {
        // The edit starts from the value shown, not from a stale entry.
        if (!isTouched) {
            setEntryText(format(value));
        }
        beginEdit();
    };

    const onTextChange = (text: string) => {
        if (refuseNonEntryCharacters && !isDecimalEntryText(text)) {
            setRefusedEntry(text);
            return;
        }
        setRefusedEntry(null);
        beginEdit();
        setEntryText(text);
        setIsTouched(true);
        if (applyWhileTyping) {
            const { accepted } = read(text);
            if (accepted !== null) {
                applyIfChanged(accepted);
            } else {
                restoreValueBeforeEdit();
            }
        }
    };

    const onBlur = () => {
        setRefusedEntry(null);
        if (isTouched) {
            const { accepted } = read(entryText);
            if (accepted !== null) {
                applyIfChanged(accepted);
            } else {
                restoreValueBeforeEdit();
            }
        }
        editRef.current = null;
        setIsTouched(false);
    };

    const apply = (next: number) => {
        editRef.current = null;
        setRefusedEntry(null);
        setIsTouched(false);
        applyIfChanged(next);
    };

    const reading = isTouched ? read(entryText) : null;
    const refusal =
        refusedEntry !== null
            ? { kind: 'notEntered' as const, entry: refusedEntry }
            : (reading?.refusal ?? null);
    return {
        text: isTouched ? entryText : format(value),
        isTouched,
        isInvalid: reading !== null && reading.accepted === null,
        accepted: reading?.accepted ?? null,
        refusal,
        onFocus,
        onTextChange,
        onBlur,
        apply,
    };
}

/**
 * The message telling the user why the entry is refused, as a react-intl
 * descriptor: the edit that was not entered, the two readings of an
 * ambiguous entry and how to type each, that the entry is no number or has
 * too many decimals (`maxFractionDigits`), or the range the field takes.
 */
export function decimalEntryRefusalMessage(
    refusal: DecimalEntryRefusal,
    {
        min,
        max,
        maxFractionDigits,
    }: { min: number; max: number; maxFractionDigits: number }
): { id: string; values?: Record<string, string | number> } {
    switch (refusal.kind) {
        case 'notEntered':
            return {
                id: 'numberInput.notEntered',
                values: { entry: refusal.entry },
            };
        case 'ambiguous':
            return ambiguousDecimalMessage(refusal.text, refusal.readings);
        case 'notANumber':
            return { id: 'enterANumber' };
        case 'tooManyDecimals':
            return {
                id: 'numberInput.maxDecimals',
                values: { max: maxFractionDigits },
            };
        case 'outOfRange':
            return { id: 'valueMustBeBetween', values: { min, max } };
    }
}
